import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { CheckSquare, Square, Loader2, CheckCircle2, X, AlertTriangle, Bookmark } from 'lucide-react';
import { esfAnBerichtHaengen } from '../lib/esfAnhaengen';
import { api } from '../lib/api';
import { ESF_SECTIONS as SECTIONS, ESF_ALL_QUESTIONS as ALL_QUESTIONS, type EsfQuestion as Question } from '../lib/esfFragebogen';

function isQuestionAnswered(q: Question, checked: Set<string>): boolean {
  return q.options.some((o) => checked.has(o.name));
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ESFFillPage() {
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId');
  // Aus „Vorlage anlegen“ im Editor geöffnet → Hinweis, wie die Vorlage gespeichert wird.
  const vorlageModus = searchParams.get('vorlage') === '1';

  const storageKey = reportId ? `esf_checked_${reportId}` : null;

  const [checked, setChecked] = useState<Set<string>>(() => {
    if (!storageKey) return new Set();
    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [countdown, setCountdown] = useState(3);
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; incomplete: number } | null>(null);
  const [vorlageStatus, setVorlageStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Aktuelle Antworten als ESF-Vorlage des Teams speichern — „Vorlage übernehmen" im Editor
  // füllt damit künftige Berichte mit einem Klick.
  const saveAsVorlage = async () => {
    setVorlageStatus('saving');
    try {
      await api.put('/api/esf-template/team', { selections: [...checked] });
      setVorlageStatus('saved');
      setTimeout(() => setVorlageStatus('idle'), 2500);
    } catch {
      setVorlageStatus('error');
    }
  };

  // Auto-close tab after saving
  useEffect(() => {
    if (status !== 'done') return;
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(interval); window.close(); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [status]);

  const toggle = (question: Question, opt: Option) =>
    setChecked((prev) => {
      const next = new Set(prev);
      const isNein = opt.label.startsWith('Nein');
      const isJa = opt.label.startsWith('Ja');

      if (next.has(opt.name)) {
        // Simple uncheck
        next.delete(opt.name);
      } else {
        next.add(opt.name);
        if (isNein) {
          // Uncheck all Ja-options of this question
          question.options.forEach((o) => {
            if (o.label.startsWith('Ja')) next.delete(o.name);
          });
        } else if (isJa) {
          // Uncheck Nein-option(s) of this question
          question.options.forEach((o) => {
            if (o.label.startsWith('Nein')) next.delete(o.name);
          });
        }
      }

      if (storageKey) {
        try { localStorage.setItem(storageKey, JSON.stringify([...next])); } catch {}
      }
      return next;
    });

  const doSave = async () => {
    if (!reportId) {
      setErrorMsg('Kein Bericht angegeben. Bitte schließen und erneut öffnen.');
      setStatus('error');
      return;
    }
    setConfirmDialog(null);
    setStatus('saving');
    try {
      await esfAnBerichtHaengen(reportId, [...checked]);
      localStorage.setItem(`esf_saved_${reportId}`, String(Date.now()));
      window.close();
    } catch (e: any) {
      setErrorMsg(e.message ?? 'Unbekannter Fehler');
      setStatus('error');
    }
  };

  const handleSave = () => {
    const incomplete = ALL_QUESTIONS.filter((q) => !isQuestionAnswered(q, checked)).length;
    if (incomplete > 0) {
      setConfirmDialog({ open: true, incomplete });
    } else {
      doSave();
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (status === 'done') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Gespeichert!</h2>
          <p className="text-slate-500 text-sm mb-1">
            Der ausgefüllte ESF-Fragebogen wurde erfolgreich an Ihren Bericht angehängt.
          </p>
          <p className="text-slate-400 text-xs mb-6">
            Tab schließt automatisch in {countdown} Sekunde{countdown !== 1 ? 'n' : ''}…
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setStatus('idle')}
              className="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
            >
              Weiter bearbeiten
            </button>
            <button
              onClick={() => window.close()}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              Jetzt schließen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Incompleteness confirm dialog */}
      {confirmDialog?.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  Fragebogen unvollständig
                </h3>
                <p className="text-sm text-slate-600">
                  {confirmDialog.incomplete === 1
                    ? '1 Frage wurde noch nicht beantwortet.'
                    : `${confirmDialog.incomplete} Fragen wurden noch nicht beantwortet.`}{' '}
                  Bitte alle Fragen beantworten, um den Fragebogen final an den Bericht anzuhängen.
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800 mb-5">
              Ihr bisheriger Fortschritt wird automatisch gespeichert. Sie können den Tab schließen
              und den Fragebogen später hier weiterbefüllen.
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <button
                onClick={() => setConfirmDialog(null)}
                className="px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
              >
                Weiter bearbeiten
              </button>
              <button
                onClick={() => window.close()}
                className="px-4 py-2.5 bg-slate-600 text-white rounded-lg text-sm font-semibold hover:bg-slate-700 transition-colors"
              >
                Verlassen (Fortschritt gespeichert)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">BAFA · ESF Plus</p>
            <h1 className="text-sm font-semibold text-slate-900 leading-tight">
              Fragebogen – Bereichsübergreifende Grundsätze des ESF Plus
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={saveAsVorlage}
            disabled={vorlageStatus === 'saving' || checked.size === 0}
            title="Diese Antworten als Vorlage speichern — im Editor per „Vorlage übernehmen“ für jeden Bericht nutzbar"
            className="inline-flex items-center gap-2 px-3 py-2.5 border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {vorlageStatus === 'saving' ? <Loader2 className="w-4 h-4 animate-spin" />
              : vorlageStatus === 'saved' ? <CheckCircle2 className="w-4 h-4 text-green-600" />
              : vorlageStatus === 'error' ? <X className="w-4 h-4 text-red-600" />
              : <Bookmark className="w-4 h-4" />}
            {vorlageStatus === 'saved' ? 'Als Vorlage gespeichert' : vorlageStatus === 'error' ? 'Fehler – erneut versuchen' : 'Als Vorlage speichern'}
          </button>
          <button
            onClick={handleSave}
            disabled={status === 'saving'}
            className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {status === 'saving' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert…</>
            ) : (
              <><CheckSquare className="w-4 h-4" /> Speichern &amp; Anhängen</>
            )}
          </button>
          </div>
        </div>
        {vorlageModus && vorlageStatus !== 'saved' && (
          <div className="bg-blue-50 border-t border-blue-200 px-4 py-2 text-sm text-blue-800">
            <div className="max-w-3xl mx-auto">
              Vorlage anlegen: Antworten ankreuzen, die für Ihre Berichte üblicherweise gelten, dann oben „Als Vorlage speichern“.
            </div>
          </div>
        )}
        {status === 'error' && (
          <div className="bg-red-50 border-t border-red-200 px-4 py-2 flex items-center gap-2 text-sm text-red-700">
            <X className="w-4 h-4 flex-shrink-0" />
            {errorMsg}
          </div>
        )}
      </div>

      {/* Form */}
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-10">
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          <strong>Hinweis:</strong> Bei Fragen mit „Ja"-Optionen sind Mehrfachantworten möglich.
          „Nein" schließt alle „Ja"-Optionen aus und umgekehrt.
          Klicken Sie anschließend oben auf „Speichern &amp; Anhängen".
        </div>

        {SECTIONS.map((section) => (
          <div key={section.title}>
            <div className="mb-6">
              <h2 className="text-base font-bold text-slate-900 pb-2 border-b-2 border-slate-800">
                {section.title}
              </h2>
            </div>

            <div className="space-y-6">
              {section.questions.map((q) => {
                const answered = isQuestionAnswered(q, checked);
                return (
                  <div
                    key={q.label}
                    className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-colors ${
                      answered ? 'border-green-200' : 'border-slate-200'
                    }`}
                  >
                    <div className={`px-5 py-3 border-b flex items-center gap-2 ${
                      answered
                        ? 'bg-green-50 border-green-200'
                        : 'bg-slate-50 border-slate-200'
                    }`}>
                      {answered
                        ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        : <div className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0" />
                      }
                      <p className="text-sm font-medium text-slate-800">{q.label}</p>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {q.options.map((opt) => {
                        const isChecked = checked.has(opt.name);
                        const isNein = opt.label.startsWith('Nein');
                        return (
                          <label
                            key={opt.name}
                            className={`flex items-center gap-3 px-5 py-3 cursor-pointer transition-colors ${
                              isChecked
                                ? isNein
                                  ? 'bg-slate-100 hover:bg-slate-100'
                                  : 'bg-blue-50 hover:bg-blue-50'
                                : 'hover:bg-blue-50'
                            }`}
                          >
                            <span className="flex-shrink-0">
                              {isChecked
                                ? <CheckSquare className={`w-5 h-5 ${isNein ? 'text-slate-600' : 'text-blue-600'}`} />
                                : <Square className="w-5 h-5 text-slate-300" />}
                            </span>
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={isChecked}
                              onChange={() => toggle(q, opt)}
                            />
                            <span className={`text-sm ${isChecked ? 'text-slate-900 font-medium' : 'text-slate-600'}`}>
                              {opt.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Bottom save button */}
        <div className="flex justify-end pt-4 pb-8">
          <button
            onClick={handleSave}
            disabled={status === 'saving'}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 shadow-md"
          >
            {status === 'saving' ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert…</>
            ) : (
              <><CheckSquare className="w-4 h-4" /> Speichern &amp; Anhängen</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
